import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  AirflowPipelineModalProps,
  PipelineMode,
  PipelineState,
  PIPELINE_MODE_OPTIONS,
  HealthStatus
} from '../types/airflow';
import {
  triggerPipelineForDate,
  checkAirflowHealth,
  getRunningPipelineForDate,
  getMostRecentPipelineForDate,
  getErrorMessage
} from '../utils/airflowApi';
import { queryKeys } from '../lib/queryClient';
import { useNotifications } from './NotificationToast';
import PipelineStatus from './PipelineStatus';
import CompletePipelineStatus from './CompletePipelineStatus';
import { formatInventoryDate } from '../utils/date';

// Processing mode options
enum ProcessingMode {
  ALL_USERS = 'all_users',
  FILTERED_USERS = 'filtered_users',
  SINGLE_USER = 'single_user'
}

const AirflowPipelineModal: React.FC<AirflowPipelineModalProps> = ({
  isOpen,
  onClose,
  selectedDate,
  selectedUserId,
  onPipelineTriggered,
  availableUsers = [],
}) => {
  const [mode, setMode] = useState<PipelineMode>(PipelineMode.FULL);
  const [processingMode, setProcessingMode] = useState<ProcessingMode>(ProcessingMode.FILTERED_USERS);
  const [singleUserId, setSingleUserId] = useState<string>(selectedUserId || '');
  const [triggerDate, setTriggerDate] = useState(selectedDate);
  const [loading, setLoading] = useState(false);
  const [health, setHealth] = useState<HealthStatus | null>(null);
  const [isRunning, setIsRunning] = useState(false);
  const [activeRunId, setActiveRunId] = useState<string | null>(null);
  const [mostRecentRunId, setMostRecentRunId] = useState<string | null>(null);
  const [statusLoading, setStatusLoading] = useState(false);

  const { showSuccess, showError, showWarning } = useNotifications();
  const queryClient = useQueryClient();

  // Stable no-op function for completed pipeline status
  const noOpStatusChange = useMemo(() => () => {}, []);

  // Update trigger date when selection changes
  useEffect(() => {
    setTriggerDate(selectedDate);
  }, [selectedDate]);

  // Keep single-user selection in sync with available users
  useEffect(() => {
    if (availableUsers.length === 0) {
      if (singleUserId !== '') {
        setSingleUserId('');
      }
      return;
    }

    const hasSelectedUser = selectedUserId && availableUsers.some((user) => user.user_id === selectedUserId);
    if (hasSelectedUser && singleUserId !== selectedUserId) {
      setSingleUserId(selectedUserId);
      return;
    }

    const singleUserExists = singleUserId && availableUsers.some((user) => user.user_id === singleUserId);
    if (!singleUserExists) {
      const fallbackId = availableUsers[0].user_id;
      if (singleUserId !== fallbackId) {
        setSingleUserId(fallbackId);
      }
    }
  }, [availableUsers, selectedUserId, singleUserId]);

  // Reset state when modal closes to prevent stale data on next open
  useEffect(() => {
    if (!isOpen) {
      setHealth(null);
      setIsRunning(false);
      setActiveRunId(null);
      setMostRecentRunId(null);
      setStatusLoading(false);
    }
  }, [isOpen]);

  // Keep the health/status check stable for hook dependencies
  const checkHealthAndStatus = useCallback(async () => {
    setStatusLoading(true);
    try {
      // Check Airflow health
      const healthStatus = await checkAirflowHealth();
      setHealth(healthStatus);

      // Check if pipeline is running for the selected date
      if (healthStatus.airflow_connected) {
        // All modes use the same DAG with different configuration parameters
        const dagId = 'daily_productivity_pipeline';

        const runningPipeline = await getRunningPipelineForDate(triggerDate, dagId);

        // Also get the most recent pipeline for this date (any state)
        const mostRecentPipeline = await getMostRecentPipelineForDate(triggerDate, dagId);

        if (runningPipeline && runningPipeline.runId) {
          setIsRunning(true);
          setActiveRunId(runningPipeline.runId);
        } else {
          setIsRunning(false);
          setActiveRunId(null);
        }

        // Set the most recent run ID for status display (even if not running)
        if (mostRecentPipeline && mostRecentPipeline.runId) {
          setMostRecentRunId(mostRecentPipeline.runId);
        } else {
          setMostRecentRunId(null);
        }
      }
      setStatusLoading(false);
    } catch (error) {
      console.error('Error checking health/status:', error);
      setHealth({
        status: 'unhealthy',
        airflow_connected: false,
        message: 'Failed to check Airflow status',
        timestamp: new Date().toISOString()
      });
      setStatusLoading(false);
    }
  }, [triggerDate]);

  // Check health and running status when modal opens or date changes
  useEffect(() => {
    // Only check status when modal first opens or date changes
    // NOT when mode changes - that causes loops
    if (isOpen) {
      setStatusLoading(true); // Start with loading state
      const timeoutId = setTimeout(() => {
        checkHealthAndStatus();
      }, 100);

      return () => clearTimeout(timeoutId);
    }
  }, [isOpen, triggerDate, checkHealthAndStatus]);

  // When mode changes, clear state and then fetch new status
  useEffect(() => {
    if (isOpen) {
      // Clear current state
      setActiveRunId(null);
      setMostRecentRunId(null);
      setIsRunning(false);
      setStatusLoading(true); // Show loading while fetching new status

      // Fetch new status after a small delay to prevent loops
      const timeoutId = setTimeout(() => {
        checkHealthAndStatus();
      }, 200);

      return () => clearTimeout(timeoutId);
    }
  }, [mode, isOpen, checkHealthAndStatus]);

  const getUserLabel = useCallback((userId: string) => {
    const entry = availableUsers.find((user) => user.user_id === userId);
    if (!entry) return userId;
    const base = entry.display_name || entry.email || entry.user_id;
    if (entry.email && entry.email !== base) {
      return `${base} (${entry.email})`;
    }
    return base;
  }, [availableUsers]);

  const handleTrigger = async () => {
    if (!health?.airflow_connected) {
      showError('Airflow Unavailable', 'Cannot trigger pipeline - Airflow is not available');
      return;
    }

    if (isRunning) {
      showWarning('Pipeline Running', `A pipeline is already running for ${triggerDate}`);
      return;
    }

    if (!triggerDate) {
      showError('No Date Selected', 'Please choose a date before triggering the pipeline.');
      return;
    }

    // Single user mode validation (dropdown ensures valid selection)
    if (processingMode === ProcessingMode.SINGLE_USER && !singleUserId) {
      showError('No User Selected', 'Please select a user for single user processing');
      return;
    }

    setLoading(true);
    try {
      // Determine processing parameters based on mode
      const filterUsers = processingMode === ProcessingMode.FILTERED_USERS;
      const userId = processingMode === ProcessingMode.SINGLE_USER ? singleUserId : undefined;
      
      const response = await triggerPipelineForDate(triggerDate, mode, filterUsers, userId);
      
      // Create descriptive success message
      let processingDesc = '';
      if (processingMode === ProcessingMode.SINGLE_USER) {
        processingDesc = ` for ${getUserLabel(singleUserId)} (ID: ${singleUserId})`;
      } else if (processingMode === ProcessingMode.FILTERED_USERS) {
        processingDesc = ` for current inventory users (${availableUsers.length || 0})`;
      } else {
        processingDesc = ' for all users';
      }
      
      showSuccess(
        'Pipeline Triggered',
        `Successfully triggered ${PIPELINE_MODE_OPTIONS.find(opt => opt.value === mode)?.label}${processingDesc} on ${triggerDate}`
      );

      setActiveRunId(response.dag_run_id);
      setIsRunning(true);
      setMostRecentRunId(response.dag_run_id); // Also update most recent

      if (onPipelineTriggered) {
        onPipelineTriggered(triggerDate, response.dag_run_id);
      }

      // Invalidate the complete pipeline status query to force a fresh fetch
      queryClient.invalidateQueries({
        queryKey: queryKeys.completePipelineStatus(triggerDate),
      });

      // Don't close modal immediately - let user see the status
    } catch (error: any) {
      console.error('Error triggering pipeline:', error);
      showError(
        'Pipeline Trigger Failed',
        getErrorMessage(error)
      );
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = useCallback((newState: PipelineState) => {
    const finalStates = [
      PipelineState.SUCCESS,
      PipelineState.FAILED,
      PipelineState.SKIPPED,
      PipelineState.UPSTREAM_FAILED
    ];

    if (finalStates.includes(newState)) {
      setIsRunning(false);

      if (newState === PipelineState.SUCCESS) {
        // Show appropriate success message based on mode
        if (mode === PipelineMode.ETL_ONLY) {
          showSuccess('ETL Complete', 'ETL processing completed successfully.');
        } else {
          showSuccess('Pipeline Complete', 'Your insights have been updated!');
        }

        if (onPipelineTriggered) {
          onPipelineTriggered(triggerDate, activeRunId || '');
        }
      }
    }
  }, [mode, onPipelineTriggered, triggerDate, activeRunId, showSuccess]);

  const handleCompletePipelineStatusChange = useCallback((status: any) => {
    if (status.overall_state === 'waiting') {
      setIsRunning(false);
      setActiveRunId(null);
      return;
    }

    const pipelineState = status.overall_state === 'success' ? PipelineState.SUCCESS :
                         status.overall_state === 'failed' ? PipelineState.FAILED :
                         status.overall_state === 'running' ? PipelineState.RUNNING :
                         PipelineState.QUEUED;
    handleStatusChange(pipelineState);
  }, [handleStatusChange]);

  const formatDate = (date: string) => (date ? formatInventoryDate(date) : 'Not set');

  const resetToCurrentDate = () => {
    setTriggerDate(selectedDate);
  };

  const getHealthIcon = () => {
    if (!health) return '⏳';
    switch (health.status) {
      case 'healthy':
        return '✅';
      case 'unhealthy':
        return '❌';
      case 'not_configured':
        return '⚙️';
      default:
        return '❓';
    }
  };

  const getHealthMessage = () => {
    if (!health) return 'Checking Airflow status...';

    switch (health.status) {
      case 'healthy':
        return 'Airflow is healthy and ready';
      case 'unhealthy':
        return health.message || 'Airflow is not responding';
      case 'not_configured':
        return health.message || 'Airflow integration is not configured';
      default:
        return 'Unknown status';
    }
  };

  if (!isOpen) return null;

  return (
    <div className="prompt-modal-backdrop" onClick={onClose}>
      <div className="prompt-modal airflow-pipeline-modal" onClick={(e) => e.stopPropagation()}>
        <div className="prompt-modal-header">
          <div className="prompt-header-info">
            <h2>Trigger Pipeline</h2>
            <div className="airflow-health-indicator">
              <span className="health-icon">{getHealthIcon()}</span>
              <span className="health-message">{getHealthMessage()}</span>
            </div>
          </div>
          <button 
            className="modal-close-btn"
            onClick={onClose}
            title="Close"
          >
            ×
          </button>
        </div>

        <div className="prompt-modal-content">
          {/* Pipeline Configuration */}
          <div className="pipeline-config-section">
            <div className="config-group">
              <label htmlFor="trigger-date">Pipeline Date:</label>
              <div className="date-input-group">
                <input
                  id="trigger-date"
                  type="date"
                  value={triggerDate}
                  onChange={(e) => setTriggerDate(e.target.value)}
                  className="date-input"
                />
                <span className="formatted-date">{formatDate(triggerDate)}</span>
                {triggerDate !== selectedDate && (
                  <button 
                    className="reset-date-btn"
                    onClick={resetToCurrentDate}
                    title="Reset to currently selected date"
                  >
                    ↻ Use Current ({formatDate(selectedDate)})
                  </button>
                )}
              </div>
              <div className="date-help">
                This date will be used as the logical date for the DAG run
              </div>
            </div>

            <div className="config-group">
              <label htmlFor="pipeline-mode">Execution Mode:</label>
              <select
                id="pipeline-mode"
                value={mode}
                onChange={(e) => setMode(e.target.value as PipelineMode)}
                className="mode-select"
              >
                {PIPELINE_MODE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <div className="mode-description">
                {PIPELINE_MODE_OPTIONS.find(opt => opt.value === mode)?.description}
                {mode === PipelineMode.FULL && (
                  <div style={{ marginTop: '8px', fontSize: '0.9em', color: '#6b7280' }}>
                    Note: Your data will be processed first, then insights generation begins automatically.
                    The entire process may take a few minutes.
                  </div>
                )}
              </div>
            </div>

            <div className="config-group">
              <label htmlFor="processing-mode">Processing Scope:</label>
              <div className="processing-mode-options">
                <label className="radio-label">
                  <input
                    type="radio"
                    name="processing-mode"
                    value={ProcessingMode.FILTERED_USERS}
                    checked={processingMode === ProcessingMode.FILTERED_USERS}
                    onChange={(e) => setProcessingMode(e.target.value as ProcessingMode)}
                  />
                  <span className="radio-text">Inventory Users ({availableUsers.length || 0})</span>
                </label>
                
                <label className="radio-label">
                  <input
                    type="radio"
                    name="processing-mode"
                    value={ProcessingMode.SINGLE_USER}
                    checked={processingMode === ProcessingMode.SINGLE_USER}
                    onChange={(e) => setProcessingMode(e.target.value as ProcessingMode)}
                  />
                  <span className="radio-text">Single User</span>
                </label>
              </div>
              
              {/* Single user selection dropdown - only show when single user mode is selected */}
              {processingMode === ProcessingMode.SINGLE_USER && (
                <div className="single-user-select">
                  <label htmlFor="single-user-email">Select User:</label>
                  <select
                    id="single-user-email"
                    value={singleUserId}
                    onChange={(e) => setSingleUserId(e.target.value)}
                    className="user-email-select"
                    disabled={availableUsers.length === 0}
                  >
                    {availableUsers.length === 0 && (
                      <option value="" disabled>
                        No users loaded
                      </option>
                    )}
                    {availableUsers.map(user => (
                      <option key={user.user_id} value={user.user_id}>
                        {getUserLabel(user.user_id)}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              
              <div className="processing-help">
                {processingMode === ProcessingMode.FILTERED_USERS && 
                  `Will process data for the ${availableUsers.length || 0} users currently visible in the inventory (faster)`
                }
                {processingMode === ProcessingMode.ALL_USERS && 
                  "Will process data for ALL users in the dataset (slower but complete)"
                }
                {processingMode === ProcessingMode.SINGLE_USER && 
                  `Will process data for ${getUserLabel(singleUserId)} (ID: ${singleUserId}) only (fastest for testing)`
                }
              </div>
            </div>

            {/* User Context Information */}
            <div className="user-context-section">
              <div className="config-group">
                <label>User Selection vs Processing:</label>
                <div className="user-context">
                  <div className="user-selection-info">
                    <strong>Selected User for Viewing:</strong> {selectedUserId}
                    <div className="user-note">
                      This user is for viewing insights after pipeline completion
                    </div>
                  </div>
                  <div className="processing-info">
                    <strong>Pipeline Will Process:</strong>
                    <div className="user-note">
                      {processingMode === ProcessingMode.SINGLE_USER && (
                        <>Only {getUserLabel(singleUserId)} (ID: {singleUserId}) - single user mode</>
                      )}
                      {processingMode === ProcessingMode.FILTERED_USERS && (
                        <>{availableUsers.length} loaded users (inventory snapshot)</>
                      )}
                      {processingMode === ProcessingMode.ALL_USERS && (
                        <>ALL users in the dataset (complete mode)</>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Pipeline Status */}
          {health?.airflow_connected && (
            <div className="pipeline-status-section">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h3>Pipeline Status for {formatDate(triggerDate)}</h3>
              </div>
              
              {/* Use CompletePipelineStatus for full mode to monitor both DAGs */}
              {mode === PipelineMode.FULL ? (
                <CompletePipelineStatus
                  key={`complete-${triggerDate}`}
                  date={triggerDate}
                  onStatusChange={handleCompletePipelineStatusChange}
                  autoRefresh={true}
                />
              ) : (
                // Use single DAG status for ETL-only mode
                <>
                  {(isRunning && activeRunId) ? (
                    <PipelineStatus
                      key={`active-${activeRunId}-${isOpen}`}
                      dagId="daily_productivity_pipeline" // All modes use ETL DAG
                      runId={activeRunId}
                      onStatusChange={handleStatusChange}
                      autoRefresh={true}
                    />
                  ) : mostRecentRunId ? (
                    <PipelineStatus
                      key={`recent-${mostRecentRunId}-${isOpen}`}
                      dagId="daily_productivity_pipeline" // All modes use ETL DAG
                      runId={mostRecentRunId}
                      onStatusChange={noOpStatusChange} // Stable no-op for completed runs - prevents infinite loops
                      autoRefresh={false}
                    />
                  ) : statusLoading ? (
                    <div className="loading-pipeline-status">
                      <p>Fetching pipeline status...</p>
                    </div>
                  ) : (
                    <div className="no-pipeline-status">
                      <p>No pipeline runs found for {formatDate(triggerDate)}</p>
                      <p>Click "Trigger Pipeline" to start a new {mode === PipelineMode.ETL_ONLY ? 'ETL-only' : 'full'} run for this date.</p>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* Warning Messages */}
          {isRunning && activeRunId && (
            <div className="warning-message">
              ⚠️ A pipeline is already running for {triggerDate}.
              Wait for it to complete before triggering another run.
            </div>
          )}

          {health?.status === 'not_configured' && (
            <div className="error-message">
              ⚙️ {health.message || 'Airflow integration is not configured. Please contact your administrator to set up the pipeline triggers.'}
            </div>
          )}

          {health?.status === 'unhealthy' && (
            <div className="error-message">
              ❌ {health.message || 'Airflow is currently unavailable. Please try again later or contact support if the issue persists.'}
            </div>
          )}
        </div>

        {/* Modal Actions */}
        <div className="modal-actions">
          <button
            className="cancel-btn"
            onClick={onClose}
          >
            {isRunning && activeRunId ? 'Close' : 'Cancel'}
          </button>
          
          <button
            className="trigger-btn"
            onClick={handleTrigger}
            disabled={
              loading ||
              !health?.airflow_connected ||
              isRunning ||
              (processingMode === ProcessingMode.SINGLE_USER && !singleUserId)
            }
          >
            {loading ? (
              <>
                Triggering...
              </>
            ) : isRunning ? (
              'Pipeline Running'
            ) : (
              `Trigger ${PIPELINE_MODE_OPTIONS.find(opt => opt.value === mode)?.label}`
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AirflowPipelineModal;
